import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Send, CheckCircle, Visibility, Reply } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const AnalyticsDashboard = () => {
    const [stats, setStats] = useState(null);
    const [topPosts, setTopPosts] = useState([]);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0],
    });

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange]);

    const fetchAnalytics = async () => {
        try {
            const token = localStorage.getItem('token');

            const [dashboardData, topPostsData] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/analytics/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        startDate: dateRange.start,
                        endDate: dateRange.end,
                    },
                }),
                axios.get(`${API_BASE_URL}/api/analytics/top-posts`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { limit: 5 },
                }),
            ]);

            setStats(dashboardData.data);
            setTopPosts(topPostsData.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    };

    const platformColors = {
        whatsapp: '#25D366',
        telegram: '#0088cc',
        instagram: '#E4405F',
        facebook: '#1877F2',
    };

    const platformData = stats?.byPlatform
        ? Object.entries(stats.byPlatform).map(([platform, data]) => ({
            name: platform.toUpperCase(),
            sent: data.sent,
            delivered: data.delivered,
            read: data.read,
            fill: platformColors[platform],
        }))
        : [];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                لوحة التحليلات
            </Typography>

            {/* Date Range Filter */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="من"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        type="date"
                        label="إلى"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            {/* Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Send sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">الرسائل المرسلة</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.totals.sent || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                                <Typography variant="h6">معدل التسليم</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.rates.delivery || 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Visibility sx={{ color: 'info.main', mr: 1 }} />
                                <Typography variant="h6">معدل القراءة</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.rates.read || 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Reply sx={{ color: 'warning.main', mr: 1 }} />
                                <Typography variant="h6">معدل الرد</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.rates.reply || 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                الأداء حسب المنصة
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={platformData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="sent" fill="#8884d8" name="مرسل" />
                                    <Bar dataKey="delivered" fill="#82ca9d" name="تم التسليم" />
                                    <Bar dataKey="read" fill="#ffc658" name="تمت القراءة" />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                التوزيع حسب المنصة
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={platformData}
                                        dataKey="sent"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label
                                    >
                                        {platformData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top Performing Posts */}
            <Card>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        أفضل المنشورات أداءً
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المنصة</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المحتوى</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>مرسل</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>تم التسليم</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>تمت القراءة</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ردود</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topPosts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                لا توجد بيانات متاحة
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    topPosts.map((post) => (
                                        <TableRow key={post._id}>
                                            <TableCell>{post.platform?.toUpperCase()}</TableCell>
                                            <TableCell>
                                                {post.post?.content?.text?.substring(0, 50) || 'N/A'}...
                                            </TableCell>
                                            <TableCell>{post.metrics.sent}</TableCell>
                                            <TableCell>{post.metrics.delivered}</TableCell>
                                            <TableCell>{post.metrics.read}</TableCell>
                                            <TableCell>{post.metrics.replied}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Container>
    );
};

export default AnalyticsDashboard;
